import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getProductSwatchesFilteredByImageFormat } from '../../utils/product/product';
import { localStorage } from '../../utils/storage/storage';

import { PRODUCT_IMAGE_FORMATS } from '../../constants/product-images';
import { SELECTED_SWATCHES } from '../../constants/storage-keys';

function getSwatchFromStorage(productCode) {
  return localStorage(SELECTED_SWATCHES)?.[productCode];
}

/**
 * @param onlyUseFirstProductCode By default, any productCode passed to this hook
 * at any time is used.  If you only want the first productCode passed to this hook
 * to be used, then set this to true.
 */
function useProductSwatchSelection(
  productCode,
  swatches,
  onlyUseFirstProductCode = false
) {
  const swatchProductCodeRef = useRef(productCode);
  const [selectedSwatch, setSelectedSwatch] = useState(() =>
    getSwatchFromStorage(productCode)
  );

  const firstSwatch = useMemo(
    () =>
      getProductSwatchesFilteredByImageFormat(
        swatches,
        PRODUCT_IMAGE_FORMATS.PRODUCT_MEDIUM
      )[0],
    [swatches]
  );

  useEffect(() => {
    if (!onlyUseFirstProductCode || !swatchProductCodeRef.current) {
      swatchProductCodeRef.current = productCode;
      const storedSwatch = getSwatchFromStorage(productCode);
      if (storedSwatch && !selectedSwatch) {
        setSelectedSwatch(storedSwatch);
      }
    }
  }, [onlyUseFirstProductCode, productCode, selectedSwatch]);

  const saveSelectedSwatch = useCallback(
    swatch => {
      const productCode = swatchProductCodeRef.current;
      if (
        !productCode ||
        (!swatch.color && !swatch.finish && !swatch.code && !swatch.hexCode)
      ) {
        return;
      }

      const newSwatch = swatches?.find(
        s =>
          s?.productCode === swatch.productCode &&
          s?.format === PRODUCT_IMAGE_FORMATS.PRODUCT_MEDIUM
      );

      const newSwatches = localStorage(SELECTED_SWATCHES) || {};
      newSwatches[productCode] = newSwatch;

      localStorage(SELECTED_SWATCHES, newSwatches);
      setSelectedSwatch(newSwatch);
    },
    [swatches]
  );

  return {
    saveSelectedSwatch,
    selectedSwatch: selectedSwatch || firstSwatch || undefined
  };
}

export default useProductSwatchSelection;
